/**
 * The story of one request (agent run), served by the control plane (ADR-0057):
 * `GET /v1/explain/runs/{id}/story` and `POST /v1/explain/runs/{id}/ask`.
 * The model is assembled from the hash-chained audit log; it never contains prompt or answer
 * text, tool arguments or comments.
 */
import type { Http } from "./http";
import type { LocalizedText } from "./types";
import { resolveLinkedRuns, type WhatHappened } from "./whatHappened";

/**
 * How a request ended, in the words Activity and the request page share
 * (platform_common.outcomes).
 */
export type RequestStatus = "answered" | "refused" | "blocked" | "rejected" | "failed" | "cancelled" | "replaced" | "waiting" | "running";
export const REQUEST_STATUSES: RequestStatus[] = ["answered", "refused", "blocked", "rejected", "failed", "cancelled", "replaced", "waiting", "running"];
export const REQUEST_STATUS_TONE: Record<RequestStatus, "success" | "processing" | "warning" | "error" | "default"> = {
  answered: "success",
  refused: "error",
  blocked: "error",
  rejected: "warning",
  failed: "error",
  cancelled: "default",
  replaced: "default",
  waiting: "processing",
  running: "processing",
};

/** Safe test (nothing really changed) / test with real actions / live (ADR-0062). */
export type RequestMode = "safe_test" | "test_real" | "live";

export type StoryActorKind = "user" | "agent" | "platform" | "model" | "tool";

export interface ActorRef {
  id: string;
  kind: StoryActorKind;
}

export interface StoryActor extends ActorRef {
  /** requester, approver, decider, agent, policy, safety, model, trigger */
  roles: string[];
}

export type StepKind =
  | "asked"
  | "approval_requested"
  | "approval_decided"
  | "policy"
  | "safety_check"
  | "model_call"
  | "tool_call"
  | "tool_compensation"
  | "retrieval"
  | "memory_read"
  | "memory_write"
  | "prompt"
  | "decision"
  | "question_asked"
  | "approval_skipped"
  /** ADR-0097: nobody decided in time (a reminder half-way, then the next role). */
  | "approval_reminded"
  | "approval_escalated"
  | "correction"
  | "limit_reached"
  /** ADR-0100: the answer claimed an action that no system call of the run did. */
  | "claim_unbacked"
  /** The conversation so far (and attached documents) given to the agent before its first step. */
  | "conversation_context"
  | "finished"
  | "failed"
  | "cancelled";

export type StepOutcome =
  | "done"
  | "reminded"
  | "escalated"
  | "waiting"
  | "waited"
  | "approved"
  | "rejected"
  | "edited"
  | "refused"
  | "recorded"
  | "allowed"
  | "passed"
  | "blocked"
  | "redacted"
  | "failed"
  | "answered"
  | "cancelled"
  | "decided"
  | "chosen"
  | "simulated"
  | "unchecked"
  | "asked_person"
  | "unanswered"
  | "rerouted"
  | "stopped"
  | "skipped";

export interface StoryReason {
  /** Plain reason code (four_eyes, policy_denied, role_missing, approval_node, …). */
  code: string | null;
  /** The original reason text (OPA deny reason, run error). */
  raw: string;
  /** The organisation rule's name, for `org_rule_refused` / `org_rule_approval` (ADR-0077). */
  rule?: string;
}

export interface StoryWhere {
  type: "run" | "case" | "task" | "audit" | "node";
  id: string;
}

export interface StoryStep {
  id: string;
  n: number;
  kind: StepKind;
  group: StoryGroupId;
  at: string | null;
  actor: ActorRef;
  target: ActorRef | null;
  outcome: StepOutcome | string;
  reason: StoryReason | null;
  facts: Record<string, unknown>;
  where: StoryWhere[];
  node: string | null;
  records: number[];
  seq: number;
  hash: string | null;
  /** Linked to the run by actor and time (older services audit without a run id). */
  correlated: boolean;
  since_previous_ms?: number | null;
  since_start_ms?: number | null;
}

export type StripKind = "asked" | "policy" | "waited" | "question" | "approved" | "rejected" | "safety" | "answered" | "refused" | "blocked" | "rejected_end" | "failed" | "cancelled" | "replaced";
export type StripStatus = "done" | "current" | "pending" | "refused" | "failed" | "unknown";

export interface StripItem {
  kind: StripKind;
  status: StripStatus;
  step_id: string | null;
  actor: ActorRef | null;
  implied?: boolean;
}

export type StoryGroupId = "people" | "agent" | "rules" | "model" | "records";

export interface StoryChild {
  id: string;
  kind: "person" | "agent" | "node" | "tool" | "policy" | "safety" | "rule" | "model" | "case" | "task" | "audit";
  ref: string;
  step_ids: string[];
  status: "done" | "waiting" | "refused" | "failed";
  roles?: string[];
}

export interface StoryGroup {
  id: StoryGroupId;
  children: StoryChild[];
  step_ids: string[];
}

export interface StoryRelation {
  from: string;
  to: string;
  kind: "group" | "member" | "step";
  step_id?: string;
}

export interface SpecGraphNode {
  id: string;
  type: string;
  label: LocalizedText | null;
  visited: boolean;
}

export interface SpecGraph {
  available: boolean;
  nodes: SpecGraphNode[];
  edges: { from: string; to: string; when?: string | null }[];
  entry: string | null;
  visited: string[];
  visited_source: "run" | "records" | null;
}

export interface StoryModel {
  id: string;
  provider: string | null;
  version: string | null;
  alias: string | null;
  hosting?: string | null;
  calls: number;
}

export interface AuditRow {
  seq: number;
  hash: string | null;
  prev_hash: string | null;
  event_type: string;
  kind: string | null;
  at: string | null;
  recorded_at: string | null;
  from: string | null;
  to: string | null;
  step_id: string | null;
  correlated: boolean;
  /** The record's allow-listed contents (ADR-0092 proof). */
  payload?: Record<string, unknown>;
}

export interface ChainCheck {
  ok: boolean;
  checked: number;
  head_hash: string | null;
  checkpoints_verified?: number;
  errors: string[];
}

export interface AuditViewModel {
  records: AuditRow[];
  lanes: string[];
  contiguous_links_ok: number;
  chain: ChainCheck | null;
}

export type StoryStatus = RequestStatus;

/** Someone reading this request (never one of its own records). */
export interface StoryView {
  seq: number;
  at: string | null;
  who: string | null;
  kind: string | null;
  hash: string | null;
}

export interface RequestStory {
  run_id: string;
  tenant?: string;
  status: StoryStatus;
  mode?: RequestMode;
  /** Where it ran (dev, test, staging, prod); null for runs before it was recorded. */
  environment?: string | null;
  /** The run that replaced this one after a correction (ADR-0084). */
  superseded_by?: string | null;
  /** System steps that did not work and had no failure path. */
  problems?: { node: string | null; tool: string | null; action: string | null; kind: string | null }[];
  masked_values?: number;
  views?: StoryView[];
  reason: StoryReason | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  requester: string | null;
  agent: {
    id: string | null;
    name: string | null;
    version: string | null;
    digest: string | null;
    description: LocalizedText | null;
    role: LocalizedText | null;
  };
  case_ids: string[];
  task_ids: string[];
  classification: string;
  actors: StoryActor[];
  models: StoryModel[];
  steps: StoryStep[];
  strip: StripItem[];
  groups: StoryGroup[];
  relations: StoryRelation[];
  spec_graph: SpecGraph;
  counts: { records: number; steps: number; people: number; models: number; signed_approvals: number; correlated: number };
  technical: {
    run_id: string;
    agent_id: string | null;
    spec_digest: string | null;
    policy_versions: string[];
    first_hash: string | null;
    last_hash: string | null;
    first_seq: number | null;
    last_seq: number | null;
    trace_ids: string[];
  };
  correlation_complete?: boolean;
  access?: { audit_view: boolean; can_ask: boolean };
  audit?: AuditViewModel | null;
}

export interface AskIn {
  question: string;
  language: "ar" | "en";
  /** Display names the page shows for ids in this story (labels only; ADR-0057). */
  names?: Record<string, string>;
}

export interface AskOut {
  answer: string;
  citations: string[];
  grounded: boolean;
  model: string;
}

const enc = encodeURIComponent;

export function explainApi(http: Http) {
  return {
    story: (runId: string, tenant?: string) => http.get<RequestStory>("controlPlane", `/v1/explain/runs/${enc(runId)}/story`, { tenant }),
    /** One request, joined: the run's own record with its signed audit log (ADR-0092). */
    whatHappened: async (runId: string, tenant?: string) => resolveLinkedRuns(await http.get<WhatHappened>("controlPlane", `/v1/explain/runs/${enc(runId)}/what-happened`, { tenant })),
    /** The auditors' signed evidence pack (zip) of one request. */
    evidence: async (runId: string, tenant?: string): Promise<{ filename: string; blob: Blob }> => {
      const q = tenant ? `?tenant=${enc(tenant)}` : "";
      const res = await http.raw("controlPlane", `/v1/explain/runs/${enc(runId)}/evidence${q}`, { headers: { Accept: "application/zip" } });
      const disposition = res.headers.get("content-disposition") ?? "";
      const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `request-${runId}-evidence.zip`;
      return { filename, blob: await res.blob() };
    },
    ask: (runId: string, body: AskIn, tenant?: string) => {
      const q = tenant ? `?tenant=${enc(tenant)}` : "";
      return http.post<AskOut>("controlPlane", `/v1/explain/runs/${enc(runId)}/ask${q}`, body);
    },
  };
}
